import * as React from 'react';
import RecordRTC, { MediaStreamRecorder } from 'recordrtc';
import { uploadVideo } from '../cloudinary';
import styled from '../styled-components';
import Button from './Button';
import FileContainer from './FileContainer';
import Webcam from './Webcam';

const displayMaxRecordingTimeMillis: number = 1000 * 60 * 2;
const autostopMaxRecordingTimeMillis: number = 1000 * 60 * 3;

interface RecordPageProps {
    onNewVideoUpload: (id: string) => void;
    tags: string;
    folder: string;
}

interface RecordPageState {
    recordRTC: RecordRTC | undefined;
    mediaStream: MediaStream | undefined;
    mediaBlob: Blob | undefined;
    muted: boolean;
    paused: boolean;
    state: State;
    progressPercent: number;
    errorMessage: string;
    recordStartMillis: number | undefined;
    fileToUpload?: File;
    fileSizeToUploadMB?: number;
}

enum State {
    NONE,
    RECORDING,
    RECORDED,
    UPLOADING,
    UPLOADED,
    FILE_TOO_BIG
}

const VideoFrame = styled.div`
    margin-bottom: 1rem;
`;

const Recording = styled.div`
    ${Button} {
        float: left;
    }
    ${Button} + div {
        float: left;
        margin-left: 1rem;
    }
`;

const ButtonWrapper = styled.div`
    ${Button} + ${Button} {
        margin-left: 1rem;
    }
`;

const captureUserMedia = async () => {
    const constraints: MediaStreamConstraints = {
        audio: true,
        video: {
            frameRate: { ideal: 24 },
            width: { min: 480, max: 640 },
            height: { min: 480, max: 640 },
            facingMode: { ideal: 'user' }
        }
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    return stream;
};

class RecordPage extends React.Component<RecordPageProps, RecordPageState> {
    constructor(props: RecordPageProps) {
        super(props);

        this.state = {
            recordRTC: undefined,
            mediaStream: undefined,
            mediaBlob: undefined,
            muted: true,
            paused: true,
            state: State.NONE,
            progressPercent: 0,
            errorMessage: '',
            recordStartMillis: undefined
        };

        this.requestUserMedia = this.requestUserMedia.bind(this);
        this.startRecording = this.startRecording.bind(this);
        this.stopRecording = this.stopRecording.bind(this);
        this.handleVideoFromBrowser = this.handleVideoFromBrowser.bind(this);
        this.handleVideoFromFilesystem = this.handleVideoFromFilesystem.bind(this);
        this.handleFileTooBig = this.handleFileTooBig.bind(this);
        this.handleOKOnFileSizeText = this.handleOKOnFileSizeText.bind(this);
        this.upload = this.upload.bind(this);
        this.play = this.play.bind(this);
    }

    public componentDidMount() {
        this.requestUserMedia();
    }

    private async requestUserMedia() {
        try {
            const stream = await captureUserMedia();
            this.setState({ mediaStream: stream, mediaBlob: undefined });
        } catch (err) {
            console.error('Could not access video camera.', err);
            this.setState({ errorMessage: 'Could not access video camera. Check your camera.' });
        }
    }

    private async startRecording() {
        try {
            const stream = await captureUserMedia();
            const recordRTC: RecordRTC = new RecordRTC(stream, {
                type: 'video',
                recorderType: MediaStreamRecorder,
                mimeType: 'video/webm',
                videoBitsPerSecond: 1024000
            });
            recordRTC.startRecording();
            const updateTimeRemaining = () => {
                setTimeout(() => {
                    if (this.state.state === State.RECORDING) {
                        this.forceUpdate();
                        updateTimeRemaining();
                    }
                }, 1000);
            };
            this.setState(
                {
                    recordRTC,
                    mediaStream: stream,
                    mediaBlob: undefined,
                    muted: true,
                    state: State.RECORDING,
                    recordStartMillis: Date.now(),
                    errorMessage: ''
                },
                () => {
                    updateTimeRemaining();
                }
            );
            setTimeout(() => {
                this.stopRecording();
            }, autostopMaxRecordingTimeMillis);
        } catch (err) {
            this.setState({ errorMessage: 'Could not start recording. Check your camera.' });
        }
    }

    private async stopRecording() {
        if (this.state.recordRTC) {
            this.state.recordRTC.stopRecording(() => {
                this.setState({
                    state: State.RECORDED,
                    mediaStream: undefined,
                    paused: true,
                    mediaBlob: this.state.recordRTC!.getBlob()
                });
            });
        }
    }

    private async handleVideoFromFilesystem(files: FileList | null) {
        if (files && files.length >= 1) {
            const file = files[0];
            const sizeMB = file.size / 1024 / 1024;
            if (sizeMB < 40) {
                this.upload(file);
            } else {
                this.handleFileTooBig(file);
            }
        }
    }

    private handleFileTooBig(file: File) {
        this.setState({ state: State.FILE_TOO_BIG, fileToUpload: file, fileSizeToUploadMB: file.size / 1024 / 1024 });
    }

    private handleOKOnFileSizeText() {
        this.setState({ state: State.NONE, fileToUpload: undefined, fileSizeToUploadMB: undefined });
    }

    private async handleVideoFromBrowser() {
        this.upload(this.state.recordRTC!.getBlob());
    }

    private async upload(data) {
        this.setState({ state: State.UPLOADING, errorMessage: '' });
        try {
            const publicId = await uploadVideo('candidate_video', this.props.folder, data, progressPercent => {
                this.setState({ progressPercent, state: State.UPLOADING });
            });
            this.setState({ progressPercent: 100, state: State.UPLOADED });
            this.props.onNewVideoUpload(publicId);
        } catch (err) {
            this.setState({ state: State.NONE, errorMessage: err.message });
        }
    }

    private async play() {
        const blob = this.state.recordRTC!.getBlob();
        this.setState({ mediaBlob: blob, paused: false, mediaStream: undefined, muted: false });
    }

    private timeRemaining(): number {
        return Math.round((displayMaxRecordingTimeMillis - (Date.now() - (this.state.recordStartMillis || 0))) / 1000);
    }

    public render() {
        return (
            <div>
                {this.state.state !== State.UPLOADING && (
                    <VideoFrame>
                        {(this.state.mediaStream || this.state.mediaBlob) && (
                            <Webcam
                                mediaBlob={this.state.mediaBlob}
                                mediaStream={this.state.mediaStream}
                                muted={this.state.muted}
                                paused={this.state.paused}
                            />
                        )}
                    </VideoFrame>
                )}

                {this.state.state === State.NONE && (
                    <div style={{ display: 'flex' }}>
                        <Button onClick={this.startRecording}>Start Recording</Button>{' '}
                        <FileContainer style={{ marginLeft: 20 }} normal>
                            Upload video file (max. file size: 40 MB)
                            <input
                                id="video_upload"
                                type="file"
                                accept="video/mp4|video/x-m4v|video/*"
                                onChange={e => this.handleVideoFromFilesystem(e.target.files)}
                            />
                        </FileContainer>
                    </div>
                )}
                {this.state.state === State.RECORDING && (
                    <Recording>
                        <Button onClick={this.stopRecording}>Stop</Button>
                        <div>The maximum recording time is over in {this.timeRemaining()} seconds.</div>
                    </Recording>
                )}
                {this.state.state === State.RECORDED && (
                    <ButtonWrapper>
                        <Button onClick={this.handleVideoFromBrowser} primary>
                            use this
                        </Button>
                        <Button onClick={this.play} normal>
                            play
                        </Button>
                        <Button onClick={this.startRecording} normal>
                            try again
                        </Button>
                    </ButtonWrapper>
                )}
                {this.state.state === State.FILE_TOO_BIG && (
                    <>
                        <div>
                            Your video file <strong>{this.state.fileToUpload && this.state.fileToUpload.name}</strong>{' '}
                            has a size of{' '}
                            <strong>
                                {this.state.fileSizeToUploadMB && this.state.fileSizeToUploadMB.toFixed(2)} MB
                            </strong>
                            . In order to minimise our carbon footprint, we have limited the size of uploads to V-Sponse
                            to 40 MB. You could try to adjust the resolution of the existing video or make a new
                            recording with lower resolution.
                        </div>
                        <div style={{ paddingTop: '1em' }}>
                            <Button onClick={this.handleOKOnFileSizeText}>OK</Button>
                        </div>
                    </>
                )}
                {this.state.state === State.UPLOADING && <div>Uploading... {this.state.progressPercent}%</div>}
                {this.state.state === State.UPLOADED && (
                    <div>
                        <Button onClick={this.startRecording}>try again</Button>
                    </div>
                )}

                {this.state.errorMessage && (
                    <h3 style={{ marginTop: 10, color: '#bb0000' }}>{this.state.errorMessage}</h3>
                )}
            </div>
        );
    }
}

export default RecordPage;



// WEBPACK FOOTER //
// ./src/components/RecordPage.tsx